/**
 * Luật thuần của màn Token AI: khoảng ngày theo giờ Việt Nam, xếp hạng, mức của một hạn mức tự đặt, form hạn mức.
 * Không dựng chuỗi hiển thị, không chọn màu.
 */
import { docSoToken } from '../../tenant/nghiep-vu/han-muc-token.ts';
import type {
  AiLimit,
  AiLimitCreate,
  AiLimitUpdate,
  ChuKyHanMucAi,
  HanhDongHanMucAi,
  PhamViHanMucAi,
  SuDungNgay,
} from '../types.ts';

const MS_NGAY = 86_400_000;
const LECH_VN = 7 * 3_600_000;
const hai = (n: number) => String(n).padStart(2, '0');
const isoNgay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Khoảng ngày chọn được ở biểu đồ theo ngày; API nhận tối đa 92 ngày. */
export const CAC_KHOANG_NGAY = [7, 30, 90] as const;
export type KhoangNgay = (typeof CAC_KHOANG_NGAY)[number];

/** `soNgay` ngày gần nhất, tính cả hôm nay theo giờ Việt Nam: `from`, `to` đúng dạng `yyyy-MM-dd` của API. */
export function khoangNgay(soNgay: number, bayGio: number): { from: string; to: string } {
  const homNay = Date.parse(`${isoNgay(bayGio + LECH_VN)}T00:00:00Z`);
  return { from: isoNgay(homNay - (soNgay - 1) * MS_NGAY), to: isoNgay(homNay) };
}

/** Điền đủ mọi ngày trong khoảng: ngày không dùng AI là cột 0, không phải ngày biến mất khỏi trục. */
export function dayDuNgay(items: readonly SuDungNgay[], from: string, to: string): SuDungNgay[] {
  const theoNgay = new Map(items.map((d) => [d.ngay, d]));
  const ra: SuDungNgay[] = [];
  const cuoi = Date.parse(`${to}T00:00:00Z`);
  for (let ms = Date.parse(`${from}T00:00:00Z`); ms <= cuoi; ms += MS_NGAY) {
    const ngay = isoNgay(ms);
    ra.push(theoNgay.get(ngay) ?? { ngay, fox_dau_vao: 0, fox_dau_ra: 0, calls: 0 });
  }
  return ra;
}

/** `2026-11-04` → `04/11`; giữ thứ tự `dd/MM` ở cả hai ngôn ngữ. */
export function ngayNgan(ngay: string): string {
  return `${ngay.slice(8, 10)}/${ngay.slice(5, 7)}`;
}

/** `2026-11-04` → `04/11/2026`. */
export function ngayDayDu(ngay: string): string {
  return `${ngayNgan(ngay)}/${ngay.slice(0, 4)}`;
}

/** Tháng hiện tại và `soThangTruoc` tháng trước đó theo giờ Việt Nam, mới nhất trước, dạng `yyyy-MM`. */
export function cacThang(bayGio: number, soThangTruoc: number): string[] {
  const vn = new Date(bayGio + LECH_VN);
  const goc = vn.getUTCFullYear() * 12 + vn.getUTCMonth();
  return Array.from({ length: soThangTruoc + 1 }, (_, i) => {
    const t = goc - i;
    return `${Math.floor(t / 12)}-${hai((t % 12) + 1)}`;
  });
}

/** `2026-11` → `11/2026`. */
export function nhanThang(thang: string): string {
  return `${thang.slice(5, 7)}/${thang.slice(0, 4)}`;
}

export interface DongXepHang {
  khoa: string | null;
  ten: string | null;
  vao: number;
  ra: number;
  tong: number;
  luot: number;
}

/**
 * Xếp giảm dần, giữ `tran` dòng đầu; phần còn lại gộp vào `khac` để tổng các dòng vẫn bằng tổng của tháng, thay vì
 * lặng lẽ bỏ đi. `soKhac` là số đối tượng đã gộp.
 */
export function xepHang(dong: readonly DongXepHang[], tran: number): { dau: DongXepHang[]; khac: DongXepHang | null; soKhac: number } {
  const sap = [...dong].sort((a, b) => b.tong - a.tong);
  const con = sap.slice(tran);
  if (con.length === 0) return { dau: sap, khac: null, soKhac: 0 };
  const cong = (lay: (d: DongXepHang) => number) => con.reduce((s, d) => s + lay(d), 0);
  return {
    dau: sap.slice(0, tran),
    khac: { khoa: null, ten: null, vao: cong((d) => d.vao), ra: cong((d) => d.ra), tong: cong((d) => d.tong), luot: cong((d) => d.luot) },
    soKhac: con.length,
  };
}

/**
 * Chỗ đặt thẻ chú thích của một dòng xếp hạng ngang, trong khung `rongKhung × caoKhung`: ngay dưới dòng để không che số
 * của chính nó, không đủ chỗ thì lên trên; canh theo đầu thanh và kẹp ngang để không thò khỏi khung, kể cả khung hẹp ở 768px.
 */
export function viTriTheDong(
  dong: { tren: number; duoi: number; dauThanh: number },
  the: { w: number; h: number },
  khung: { rongKhung: number; caoKhung: number },
): { left: number; top: number } {
  const KHE = 6;
  const canDuoi = dong.duoi + KHE + the.h <= khung.caoKhung || dong.tren - KHE - the.h < 0;
  const left = Math.min(Math.max(dong.dauThanh - the.w / 2, 0), Math.max(0, khung.rongKhung - the.w));
  return { left, top: canDuoi ? dong.duoi + KHE : dong.tren - KHE - the.h };
}

/** Mức của một hạn mức tự đặt: từ 80% cảnh báo, chạm trần là hết. */
export type MucHanMucAi = 'binh-thuong' | 'canh-bao' | 'het';

export function mucHanMucAi(q: Pick<AiLimit, 'da_dung' | 'gioi_han'>): MucHanMucAi {
  if (q.da_dung >= q.gioi_han) return 'het';
  return q.da_dung * 100 >= q.gioi_han * 80 ? 'canh-bao' : 'binh-thuong';
}

export interface FormHanMucAi {
  phamVi: PhamViHanMucAi;
  khoa: string | null;
  tinhNang: string | null;
  chuKy: ChuKyHanMucAi;
  gioiHan: string;
  hanhDong: HanhDongHanMucAi;
}

export const FORM_TRONG: FormHanMucAi = {
  phamVi: 'DON_VI',
  khoa: null,
  tinhNang: null,
  chuKy: 'THANG',
  gioiHan: '',
  hanhDong: 'CHAN',
};

export function formTuHanMuc(h: AiLimit): FormHanMucAi {
  return {
    phamVi: h.pham_vi,
    khoa: h.khoa,
    tinhNang: h.tinh_nang,
    chuKy: h.chu_ky,
    gioiHan: String(h.gioi_han),
    hanhDong: h.hanh_dong,
  };
}

export type LoiGioiHan = 'trong' | 'sai' | 'qua-lon' | 'khong';
export interface LoiFormHanMucAi {
  khoa?: true;
  gioiHan?: LoiGioiHan;
}

/** Hạn mức 0 token là khoá hẳn đối tượng, không phải trần: bắt nhập số dương. */
export function kiemFormHanMucAi(f: FormHanMucAi): LoiFormHanMucAi {
  const so = docSoToken(f.gioiHan);
  return {
    ...(f.khoa ? {} : { khoa: true as const }),
    ...(so.ok ? (so.so > 0 ? {} : { gioiHan: 'khong' as const }) : { gioiHan: so.loi }),
  };
}

/** Thân tạo. Gọi sau `kiemFormHanMucAi`. */
export function thanTaoHanMuc(f: FormHanMucAi): AiLimitCreate {
  const so = docSoToken(f.gioiHan);
  return {
    pham_vi: f.phamVi,
    khoa: f.khoa ?? '',
    tinh_nang: f.tinhNang,
    chu_ky: f.chuKy,
    gioi_han: so.ok ? so.so : 0,
    hanh_dong: f.hanhDong,
    bat: true,
  };
}

/** Thân sửa: chỉ trường đã đổi. Phạm vi và đối tượng không đổi được, muốn đổi thì tạo hạn mức mới. */
export function thanSuaHanMuc(cu: AiLimit, f: FormHanMucAi): AiLimitUpdate {
  const so = docSoToken(f.gioiHan);
  return {
    ...(f.tinhNang !== cu.tinh_nang ? { tinh_nang: f.tinhNang } : {}),
    ...(f.chuKy !== cu.chu_ky ? { chu_ky: f.chuKy } : {}),
    ...(so.ok && so.so !== cu.gioi_han ? { gioi_han: so.so } : {}),
    ...(f.hanhDong !== cu.hanh_dong ? { hanh_dong: f.hanhDong } : {}),
  };
}
